import React from 'react';
import Layout from '../components/Layout';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AutoHeight from 'embla-carousel-auto-height';
import Autoplay from 'embla-carousel-autoplay';
import { toast } from "sonner";
import { 
  TrendingUp, 
  Users, 
  Lightbulb, 
  Search, 
  DollarSign, 
  Globe, 
  Cpu, 
  GraduationCap, 
  Briefcase,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

import heroTeam from "../assets/hero_team.jpg";
import uiHome from "../assets/ui_home.png";
import uiOffers from "../assets/ui_offers.png";
import uiDashboard from "../assets/ui_dashboard.png";
import uiCompanies from "../assets/ui_companies.png";
import mapWorld from "../assets/map_world.png";
import flagKorea from "../assets/flag_korea.png";
import flagMalaysia from "../assets/flag_malaysia.png";
import flagSingapore from "../assets/flag_singapore.png";
import flagChina from "../assets/flag_china.png";
import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const InterfaceItem = ({ item, i }) => {
  return (
    <div className={`flex flex-col lg:flex-row gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
      <div className="w-full lg:w-1/2">
        <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200 bg-white group hover:shadow-3xl transition-all duration-500">
          <img 
            src={item.img} 
            alt={item.title} 
            className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700" 
          />
        </div>
      </div>
      <div className="w-full lg:w-1/2 space-y-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 font-bold text-lg mb-2">
          {i + 1}
        </div>
        <h3 className="text-3xl font-bold text-slate-900">{item.title}</h3>
        <p className="text-xl text-slate-600 leading-relaxed">{item.text}</p>
      </div>
    </div>
  );
};

const HomeJP = () => {
  const handleDemoClick = (action) => {
    toast.info(`${action} 機能は近日公開予定です！`, {
      description: "これはデモサイトです。"
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 z-10">
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-500"></span>
                </span>
                プレシードスタートアップ
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                フランスのトップ<span className="text-red-600">エンジニアリング</span>、<span className="text-red-600">ビジネス</span>、<span className="text-red-600">セールス</span>、<span className="text-red-600">マーケティング</span>、<span className="text-red-600">観光</span> & <span className="text-red-600">ホスピタリティ</span>インターンを採用
              </h1>
              <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
                フランスのトップ大学から厳選された学生プールにアクセス。貴社のチームに参加する準備ができた、優秀で意欲的な候補者を見つけましょう。
              </p>
              <div className="flex flex-col gap-3 pt-2">
                <p className="text-lg font-medium text-slate-700 italic">
                  "Empowering Interns. Elevating Employers."
                </p>
              </div>
            </div>
            <div className="relative lg:h-[600px] w-full rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={heroTeam} 
                alt="Diverse team collaborating in Tokyo office" 
                className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem / Data-Driven Hook */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">日本の深刻な人材不足を解決する</h2>
            <p className="text-lg text-slate-600">
              経験豊富な人材を対象とした従来の採用手法だけでは、もはや十分ではありません。Internjinは、フランスの高度なスキルを持つグローバル志向の学生人材への扉を開きます。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">有効求人倍率 1.28倍</h3>
                <p className="text-slate-600">
                  日本の有効求人倍率は依然として高く、優秀な国内人材の獲得競争は激化しています。
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-slate-50">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">79万人のIT人材不足</h3>
                <p className="text-slate-600">
                  経済産業省は2030年までに最大79万人のIT人材が不足すると予測しています。企業の成長は、新たな人材パイプラインの開拓にかかっています。
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Intern Advantage */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">なぜインターンなのか？ <br/><span className="text-red-500">成長のための賢い戦略。</span></h2>
            <p className="text-lg text-slate-300 max-w-2xl">
              経験豊富な候補者が希少で高コストな市場において、トップクラスの学生人材は、より柔軟で費用対効果の高い、強力なソリューションを提供します。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <GraduationCap className="h-6 w-6 text-red-400" />,
                title: "未来のリーダーを育成",
                desc: "初日から貴社独自の手法や文化に合わせてトレーニングできます。"
              },
              {
                icon: <Lightbulb className="h-6 w-6 text-red-400" />,
                title: "イノベーションを注入",
                desc: "学生はAI、データサイエンス、グローバルビジネスモデルに関する最新の知識をもたらします。"
              },
              {
                icon: <Search className="h-6 w-6 text-red-400" />,
                title: "採用リスクの低減",
                desc: "フルタイムのオファーを出す前に、スキルやカルチャーフィットを見極めることができます。"
              },
              {
                icon: <DollarSign className="h-6 w-6 text-red-400" />,
                title: "費用対効果",
                desc: "シニア採用のような高コストをかけずに、特定のプロジェクトに高度なスキルを確保できます。"
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-red-500/50 transition-colors">
                <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4 border border-slate-700">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">ソリューション</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">フランスと日本をつなぐ唯一の採用プラットフォーム</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              {
                title: "希少な人材へのアクセス",
                desc: "フランスのトップ大学（ビジネス、エンジニアリング、セールス、マーケティング、観光、ホスピタリティ）の10,000人以上の学生にアクセス。",
                icon: <Globe className="h-6 w-6 text-white" />
              },
              {
                title: "AIスマートマッチング",
                desc: "貴社の条件に完全に一致する候補者のショートリストを即座に受け取れます。",
                icon: <Cpu className="h-6 w-6 text-white" />
              },
              {
                title: "カルチャーオンボーディング",
                desc: "人材を見つけるだけではありません。すべての候補者は「日本で働く」プログラムを修了します。",
                icon: <Briefcase className="h-6 w-6 text-white" />
              },
              {
                title: "採用ブランディング",
                desc: "カスタマイズされた企業ページで貴社独自の文化を紹介し、候補者を惹きつけます。",
                icon: <Users className="h-6 w-6 text-white" />
              }
            ].map((feature, i) => (
              <div key={i} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 h-14 w-14 rounded-2xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/20 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Website Interface Overview */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">ウェブサイトインターフェース概要</h2>
            <p className="text-lg text-slate-600">
              効率性のために設計されたモダンで合理化されたプラットフォームを体験してください。
            </p>
          </div>

          <div className="space-y-24">
            {[
              {
                img: uiHome,
                text: "直感的でユーザー中心のホームページインターフェース。",
                title: "ホームページ"
              },
              {
                img: uiOffers,
                text: "厳選された求人とスムーズなナビゲーションを備えたダイナミックな求人ボード。",
                title: "求人ボード"
              },
              {
                img: uiDashboard,
                text: "シームレスな人材獲得管理のための包括的なバックエンドインターフェース。",
                title: "リクルーターダッシュボード"
              },
              {
                img: uiCompanies,
                text: "トップ雇用主：現在採用中の業界リーダーを探索。",
                title: "企業ディレクトリ"
              }
            ].map((item, i) => (
              <InterfaceItem key={i} item={item} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-16">学生採用を成功させる4つのステップ</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-8 left-0 w-full h-0.5 bg-slate-200 -z-10 transform translate-y-4"></div>

            {[
              { step: "01", title: "掲載", desc: "インターンシップ/求人を作成し、企業プロフィールを構築します。" },
              { step: "02", title: "マッチング", desc: "AIがマッチングした意欲的な候補者のリストを即座に受け取ります。" },
              { step: "03", title: "採用", desc: "最適なプロフィールと面接を行い、選考します。" },
              { step: "04", title: "オンボード", desc: "文化的な事前トレーニングとフォローアップは当社が担当します。" }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 relative">
                <div className="text-5xl font-bold text-slate-100 absolute top-4 right-4">{item.step}</div>
                <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold mb-6 relative z-10">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Difference */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">採用で終わりではありません</h2>
            <p className="text-lg text-slate-600 mb-12 text-center">
              採用の成功は、統合の成功にかかっています。私たちの候補者は「ただの学生」ではなく、準備のできたプロフェッショナルです。
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">候補者向け</h4>
                    <p className="text-slate-600 leading-relaxed">日本のビジネスマナー（報連相など）、職場の期待、文化に関する包括的なトレーニング。到着後1ヶ月のフォローアップを含みます。</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">企業様向け</h4>
                    <p className="text-slate-600 leading-relaxed">より準備ができ、自律的で、すぐに戦力となるチームメンバーを受け入れることができます。これにより、人事の負担が大幅に軽減され、定着率が向上します。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section data-testid="success-story-section" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">サクセスストーリー</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">国境を越えて日本へ</h2>
            <p className="text-lg text-slate-600">
              フランスのエンジニアリングインターンが、日本での6ヶ月間の配置を人生を変える経験に変えた物語をご覧ください。
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-72 lg:h-auto">
                  <img 
                    src={thomasPortrait} 
                    alt="リヨン出身のエンジニアリングインターン、トマ" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent lg:bg-gradient-to-r"></div>
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6">
                    <p className="text-white font-bold text-xl">Thomas</p>
                    <p className="text-white/80 text-sm">リヨン出身のソフトウェアエンジニアリングインターン</p>
                  </div>
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <blockquote className="text-lg text-slate-700 italic leading-relaxed mb-6 border-l-4 border-red-500 pl-4">
                    "このインターンシップを通じて、チームワークの重要性と問題を協力して解決する方法を学びました。日本に来ることを選んで本当に良かったです。"
                  </blockquote>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">フランス・リヨン出身のエンジニアリング学生</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">日本のスタートアップでiOS開発インターン</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-600">日本文化への好奇心から日本を選択</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 bg-slate-50 p-6 lg:p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasExhibition} alt="展示会でのトマ" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">実践的なインパクト</p>
                      <p className="text-slate-500 text-sm">iOSアプリを開発し、展示会に参加</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={thomasFushimi} alt="伏見稲荷大社" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">文化体験</p>
                      <p className="text-slate-500 text-sm">京都、伏見稲荷などを探訪</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center justify-start md:justify-end">
                    <a 
                      href="https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      data-testid="read-full-story-link"
                      className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
                    >
                      記事全文を読む
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-6xl mx-auto">
            {[
              {
                name: "Grégoire",
                role: "交換留学生（エンジニアリング）",
                school: "ICAM ストラスブール・ヨーロッパ",
                location: "芝浦工業大学、東京",
                quote: "日本の大学は最先端の科学、ロボティクス、AIを探求する機会を提供します。アドバイス：迷わず行きましょう！",
                img: gregoireJapan,
                link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
              },
              {
                name: "Diego",
                role: "データアナリストインターン",
                school: "UCLy / ESTRI リヨン",
                location: "MSCジャパン、東京",
                quote: "語学と異文化のコースを実際のプロフェッショナルな場面で実践できました。より自律的で適応力のある人間になって帰国しました。",
                img: diegoTokyo,
                link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
              },
              {
                name: "Yoann",
                role: "組み込みシステムインターン＆映画監督",
                school: "CESI トゥールーズ",
                location: "IoTスタートアップ、渋谷、東京",
                quote: "一人で地球の裏側に旅することは不安定ですが、これらの経験こそが本当の成長を可能にします。",
                img: yoannJapan,
                link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
              },
              {
                name: "Mirabelle",
                role: "化学エンジニアリングインターン",
                school: "CPE リヨン",
                location: "エア・リキード研究所、つくば",
                quote: "海外に出ることは最高の選択でした！自分の人生や当たり前だと思っていたことを見つめ直す機会を与えてくれます。",
                img: mirabelleJapan,
                link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
              }
            ].map((story, i) => (
              <a 
                key={i}
                href={story.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`story-card-${story.name.toLowerCase()}`}
                className="group bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden hover:shadow-xl hover:border-red-200 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={story.img} 
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white font-bold text-lg">{story.name}</p>
                    <p className="text-white/80 text-xs">{story.school}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{story.role}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">{story.location}</p>
                  <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3">
                    "{story.quote}"
                  </blockquote>
                  <div className="mt-4 flex items-center gap-1 text-red-600 text-sm font-semibold group-hover:gap-2 transition-all">
                    記事を読む <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Business Models */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">ゼロリスク、高い柔軟性</h2>
            <p className="text-lg text-slate-600">貴社の戦略に合ったプランをお選びください。複雑な料金や隠れたコストはありません。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card 
              className="border-2 border-slate-200 hover:border-red-600 transition-colors cursor-pointer group"
              onClick={() => handleDemoClick("年間サブスクリプション")}
            >
              <CardHeader>
                <CardTitle className="text-2xl group-hover:text-red-600 transition-colors">年間サブスクリプション</CardTitle>
                <CardDescription>継続的な採用ニーズに最適</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> ベーシック、スタンダード、エンタープライズプラン
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> 毎月の求人掲載
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> 専任アカウントマネージャー
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card 
              className="border-2 border-red-600 bg-white shadow-xl relative overflow-hidden cursor-pointer"
              onClick={() => handleDemoClick("成果報酬型")}
            >
              <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">人気</div>
              <CardHeader>
                <CardTitle className="text-2xl text-red-600">成果報酬型</CardTitle>
                <CardDescription>採用ごとの支払いモデル</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> 初期費用なし
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> 採用成功時のみお支払い
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-green-500" /> リスクフリーのトライアル期間
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">FAQ</h2>
          
          <Tabs defaultValue="Internjin全般について" className="space-y-8">
            <div className="flex justify-center overflow-x-auto pb-4 scrollbar-hide">
              <TabsList className="bg-slate-100 p-1 rounded-full border border-slate-200 h-auto inline-flex">
                {[
                  "Internjin全般について",
                  "学生について",
                  "ビジネスモデルについて",
                  "手続きについて"
                ].map((cat) => (
                  <TabsTrigger 
                    key={cat} 
                    value={cat}
                    className="rounded-full px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-red-600 data-[state=active]:shadow-sm text-slate-600 transition-all"
                  >
                    {cat.replace("について", "")}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {[
            {
              category: "Internjin全般について",
              items: [
                {
                  q: "Internjinは採用代理店ですか？",
                  a: "いいえ。Internjinは教育サポートの要素を持つ、インターンシップ専用のマッチングプラットフォームです。目的は研修と異文化交流であり、正規雇用の斡旋ではありません。"
                },
                {
                  q: "Internjinはいつ設立されましたか？",
                  a: "Internjinは、自身の日本でのインターンシップ経験をもとに2人のフランス人学生が設立した、ローンチフェーズのスタートアップです。"
                },
                {
                  q: "フランスの機関から支援を受けていますか？",
                  a: "Internjinはフランスの高等教育機関と連携して活動しており、積極的に機関パートナーシップを求めています。このモデルはフランスのインターンシップに関する法的要件に完全に準拠しています。"
                }
              ]
            },
            {
              category: "学生について",
              items: [
                {
                  q: "どのレベルの学生を紹介してもらえますか？",
                  a: "学生はフランスのビジネススクール、エンジニアリングスクール、大学および専門機関出身で、学士から修士レベルまで、テック、デザイン、マーケティング、財務、人事などの分野に対応しています。"
                },
                {
                  q: "学生は日本語を話しますか？",
                  a: "話す学生もいますが、必須ではありません。英語で業務ができる学生も選考対象です。応募者は語学基準でフィルタリングでき、全学生は出発前に日本に関する文化的準備を受けます。"
                },
                {
                  q: "学生は一人で日本に来ますか？",
                  a: "はい。学生は事前にInternjinのサポートを受けながら、自身で渡航と宿泊を手配します。Internjinは企業と現地リソースを結びつけ、学生の受け入れと統合を円滑にすることも可能です。"
                },
                {
                  q: "インターンシップがうまくいかない場合はどうなりますか？",
                  a: "Internjinが調停者として迅速に介入し、双方にとって満足のいく解決策を見つけます。極端な場合、協定の条件に従ってインターンシップを早期終了することも可能です。"
                },
                {
                  q: "フランス人学生は日本の職場文化にどう対応しますか？",
                  a: "フランス人学生は直接的で積極的な傾向があり、日本の規範と異なる場合があります。全学生はコミュニケーションスタイル、階層、時間厳守、ビジネスマナーを含む異文化準備を受けます。Internjinはインターンシップ全期間を通じて双方をサポートします。"
                },
                {
                  q: "学生が日本で事故にあったり病気になった場合はどうなりますか？",
                  a: "学生は出発前に個人の健康保険と旅行保険を手配する必要があります。企業は個人の健康保障に責任を負いません。労働災害はインターンシップ協定に記載された責任に従い、Internjinが双方にアドバイスを提供できます。"
                },
                {
                  q: "インターンシップ後に学生を正式採用できますか？",
                  a: "もちろんです。実際に起こっています。双方が希望する場合、企業はインターンシップ終了時に正社員のポジションを提供できます。Internjinはこの結果を全面的にサポートします。"
                }
              ]
            },
            {
              category: "ビジネスモデルについて",
              items: [
                {
                  q: "企業側のコストはどのくらいですか？",
                  a: "日本法上、学生への報酬支払い義務はありませんが、優秀な候補者を引き付けるために手当の提供を強くお勧めします。マッチングとフォローアップのためのサービス料もInternjinにお支払いいただきます。詳細な料金体系は初回ミーティングでご提供します。"
                },
                {
                  q: "学生は企業の従業員になりますか？",
                  a: "いいえ。学生はインターンシップ協定によって管理され、雇用契約は不要です。インターンシップ協定が唯一必要な法的文書です。"
                },
                {
                  q: "インターンシップの最短・最長期間はどのくらいですか？",
                  a: "インターンシップは、学生の都合、企業のニーズ、プロジェクトの範囲に応じて2ヶ月から1年まで対応しています。"
                }
              ]
            },
            {
              category: "手続きについて",
              items: [
                {
                  q: "企業はどのような手続きを行う必要がありますか？",
                  a: "ニーズと求めるプロフィールを定義し、インターンシップ協定に署名し、社内メンターを指定して学生を迎えてください。候補者の選考、書類管理、継続的なサポートはInternjinが担当します。"
                },
                {
                  q: "マッチングプロセスにはどのくらい時間がかかりますか？",
                  a: "選考とマッチングは通常、求人掲載から2〜4週間かかります。ビザ手続きや学事カレンダーを考慮して、希望する開始日の少なくとも6〜8週間前に計画することをお勧めします。"
                },
                {
                  q: "うまくいった場合、インターンシップを延長または更新できますか？",
                  a: "はい。学生の学業スケジュールの範囲内で、双方が同意すれば可能です。延長は元のインターンシップ協定の補足書として正式化する必要があり、Internjinが管理します。"
                },
                {
                  q: "企業はフランス語を話す必要がありますか？",
                  a: "いいえ。Internjinがバイリンガルで仲介し、全書類は英語または日本語で提供可能です。"
                },
                {
                  q: "Internjinとのパートナーシップを始めるにはどうすればよいですか？",
                  a: "お気軽にご連絡いただき、無料の初回相談をご予約ください。チームが企業のニーズに合わせたご提案を作成いたします。"
                }
              ]
            }
          ].map((category) => (
              <TabsContent key={category.category} value={category.category} className="animate-in fade-in-50 slide-in-from-bottom-2 duration-300">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {category.items.map((item, j) => (
                    <AccordionItem key={j} value={`item-${j}`} className="border rounded-xl px-6 bg-slate-50 hover:bg-slate-100 transition-colors border-slate-100 shadow-sm data-[state=open]:bg-white data-[state=open]:shadow-md data-[state=open]:border-red-100">
                      <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-red-600 hover:no-underline py-4 text-lg">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed pb-6 text-base">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Founders */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">創業者紹介</h2>
            <p className="text-lg text-slate-600">
              日本でのインターン経験者が、その道のりを理解して構築しました。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* P. Theotime */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  PT
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">P. Theotime</h3>
                <p className="text-red-600 font-medium mb-6">共同創業者</p>
                
                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">ロボット工学修士</p>
                      <p className="text-sm text-slate-500">CPEリヨン（フランス）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">6ヶ月インターンシップ</p>
                      <p className="text-sm text-slate-500">BMWジャパン（東京）</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* D. Chahan */}
            <Card className="border-none shadow-lg bg-slate-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="h-24 w-24 bg-slate-200 rounded-full flex items-center justify-center mb-6 text-2xl font-bold text-slate-600 border-4 border-white shadow-sm">
                  DC
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">D. Chahan</h3>
                <p className="text-red-600 font-medium mb-6">共同創業者</p>
                
                <div className="space-y-4 text-slate-600 w-full">
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <GraduationCap className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">ロボット工学修士</p>
                      <p className="text-sm text-slate-500">CPEリヨン（フランス）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-left bg-white p-4 rounded-lg border border-slate-100">
                    <Briefcase className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">6ヶ月インターンシップ</p>
                      <p className="text-sm text-slate-500">Sound & Vision Technologies（大阪）</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
           <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
           <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-red-500 text-red-400">今後のロードマップ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">ネットワークをヨーロッパ全土へ拡大</h2>
            <p className="text-xl text-slate-300 font-light">
              私たちのミッションはフランスに留まりません。日本企業とヨーロッパの最高峰大学をつなぐ究極の架け橋を構築します。
            </p>
            <div className="relative w-full max-w-5xl mx-auto mt-16 aspect-[16/9] rounded-xl overflow-hidden bg-slate-50 border border-slate-200 shadow-xl group">
              <img 
                src={mapWorld} 
                alt="World Map" 
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* East Asia Expansion */}
            <div className="mt-24 text-center">
              <Badge variant="outline" className="mb-4 border-blue-500 text-blue-400">フェーズ 2</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">東アジアへの拡大</h2>
              <p className="text-xl text-slate-300 font-light max-w-3xl mx-auto mb-12">
                私たちのビジョンには、ヨーロッパと韓国、マレーシア、シンガポール、中国の活気ある経済をつなぐことが含まれています。
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: "韓国", img: flagKorea },
                  { name: "マレーシア", img: flagMalaysia },
                  { name: "シンガポール", img: flagSingapore },
                  { name: "中国", img: flagChina }
                ].map((country, i) => (
                  <div key={i} className="group relative rounded-xl overflow-hidden aspect-square border border-slate-700 bg-slate-800 shadow-lg hover:shadow-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
                    <img src={country.img} alt={country.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent p-4">
                      <p className="font-bold text-white text-lg">{country.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomeJP;
